import apiService from './apiService';

export interface SendOTPRequest {
  mobileNumber: string;
  fullName: string;
}

export interface SendOTPResponse {
  success: boolean;
  message: string;
  data?: {
    mobileNumber: string;
    fullName: string;
    expiry: number;
    otp?: string; // Only in development
  };
  error?: string;
}

export interface VerifyOTPRequest {
  mobileNumber: string;
  otp: string;
}

export interface VerifyOTPResponse {
  success: boolean;
  message: string;
  data?: {
    mobileNumber: string;
    fullName: string;
    verifiedAt: string;
  };
  error?: string;
}

export interface ResendOTPRequest {
  mobileNumber: string;
  fullName: string;
}

export interface ResendOTPResponse {
  success: boolean;
  message: string;
  data?: {
    mobileNumber: string;
    fullName: string;
    expiry: number;
    otp?: string; // Only in development
  };
  error?: string;
}

export interface OTPStatusResponse {
  success: boolean;
  data?: {
    exists: boolean;
    isExpired?: boolean;
    remainingTime?: number;
    attempts?: number;
    fullName?: string;
    createdAt?: string;
    otp?: string; // Only in development
  };
  error?: string;
}

class OTPService {
  /**
   * Send OTP to mobile number
   */
  async sendOTP(request: SendOTPRequest): Promise<SendOTPResponse> {
    try {
      console.log('📱 Sending OTP request:', request);
      const response = await apiService.post('/otp/send-otp', request);
      
      if (response.success) {
        console.log('✅ OTP sent successfully:', response.data);
        return {
          success: true,
          message: response.data.message,
          data: response.data
        };
      } else {
        console.error('❌ Failed to send OTP:', response.error);
        return {
          success: false,
          message: 'Failed to send OTP',
          error: response.error || 'Failed to send OTP'
        };
      }
    } catch (error: any) {
      console.error('❌ Error sending OTP:', error);
      return {
        success: false,
        message: 'Network error while sending OTP',
        error: error.message || 'Network error while sending OTP'
      };
    }
  }

  /**
   * Verify OTP
   */
  async verifyOTP(request: VerifyOTPRequest): Promise<VerifyOTPResponse> {
    try {
      console.log('🔐 Verifying OTP request:', request);
      const response = await apiService.post('/otp/verify-otp', request);
      
      if (response.success) {
        console.log('✅ OTP verified successfully:', response.data);
        return {
          success: true,
          message: response.data.message,
          data: response.data
        };
      } else {
        console.error('❌ Failed to verify OTP:', response.error);
        return {
          success: false,
          message: 'Failed to verify OTP',
          error: response.error || 'Failed to verify OTP'
        };
      }
    } catch (error: any) {
      console.error('❌ Error verifying OTP:', error);
      return {
        success: false,
        message: 'Network error while verifying OTP',
        error: error.message || 'Network error while verifying OTP'
      };
    }
  }

  /**
   * Resend OTP
   */
  async resendOTP(request: ResendOTPRequest): Promise<ResendOTPResponse> {
    try {
      console.log('🔄 Resending OTP request:', request);
      const response = await apiService.post('/otp/resend-otp', request);
      
      if (response.success) {
        console.log('✅ OTP resent successfully:', response.data);
        return {
          success: true,
          message: response.data.message,
          data: response.data
        };
      } else {
        console.error('❌ Failed to resend OTP:', response.error);
        return {
          success: false,
          message: 'Failed to resend OTP',
          error: response.error || 'Failed to resend OTP'
        };
      }
    } catch (error: any) {
      console.error('❌ Error resending OTP:', error);
      return {
        success: false,
        message: 'Network error while resending OTP',
        error: error.message || 'Network error while resending OTP'
      };
    }
  }

  /**
   * Get OTP status (for debugging)
   */
  async getOTPStatus(mobileNumber: string): Promise<OTPStatusResponse> {
    try {
      console.log('📊 Getting OTP status for:', mobileNumber);
      const response = await apiService.get(`/otp/status/${mobileNumber}`);
      
      if (response.success) {
        console.log('✅ OTP status retrieved:', response.data);
        return {
          success: true,
          data: response.data
        };
      } else {
        console.error('❌ Failed to get OTP status:', response.error);
        return {
          success: false,
          data: undefined,
          error: response.error || 'Failed to get OTP status'
        };
      }
    } catch (error: any) {
      console.error('❌ Error getting OTP status:', error);
      return {
        success: false,
        data: undefined,
        error: error.message || 'Network error while getting OTP status'
      };
    }
  }
}

const otpService = new OTPService();
export default otpService;
